import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  ArrowLeftIcon,
  MapPinIcon,
  ClockIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  PhotoIcon,
  FlagIcon,
  PlayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import toast from 'react-hot-toast';
import TripMap from './TripMap';

const theme = {
  colors: {
    background: {
      primary: '#0f172a',
      secondary: '#1e293b',
      tertiary: '#334155',
      card: '#1e293b'
    },
    text: {
      primary: '#f8fafc',
      secondary: '#cbd5e1',
      disabled: '#64748b'
    },
    primary: {
      main: '#3b82f6'
    },
    border: {
      primary: '#334155'
    }
  },
  borderRadius: {
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px'
  }
};

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${theme.colors.background.primary};
  padding: 2rem 1rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Logo = styled.div`
  display: inline-block;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  padding: 1rem 2rem;
  border-radius: ${theme.borderRadius.xl};
  margin-bottom: 1.5rem;
  box-shadow: 0 10px 30px rgba(59, 130, 246, 0.3);
`;

const LogoText = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin: 0;
  letter-spacing: -0.5px;
`;

const SubTitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: 1rem;
  margin: 0;
`;

const Card = styled.div`
  background: ${theme.colors.background.card};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  margin-bottom: 1.5rem;
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.secondary};
`;

const CardTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
  font-size: 1.5rem;
  color: ${theme.colors.text.primary};
  
  svg {
    width: 1.75rem;
    height: 1.75rem;
    color: ${theme.colors.primary.main};
  }
`;

const CardContent = styled.div`
  padding: 1.5rem;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${props => {
    switch (props.status) {
      case 'active': return 'rgba(34, 197, 94, 0.2)';
      case 'completed': return 'rgba(59, 130, 246, 0.2)';
      default: return 'rgba(100, 116, 139, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'active': return '#4ade80';
      case 'completed': return '#60a5fa';
      default: return '#94a3b8';
    }
  }};
  border-radius: ${theme.borderRadius.full};
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: capitalize;
  
  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const TripMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${theme.colors.border.primary};
  font-size: 0.9375rem;
  color: ${theme.colors.text.secondary};
  
  span {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
`;

const StatItem = styled.div`
  background: ${theme.colors.background.tertiary};
  padding: 1.25rem;
  border-radius: ${theme.borderRadius.md};
  text-align: center;
  border: 1px solid ${theme.colors.border.primary};
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #60a5fa;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 500;
`;

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
`;

const PhotoItem = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  border: 1px solid ${theme.colors.border.primary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.05);
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const WaypointsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const WaypointItem = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: ${theme.colors.background.tertiary};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.primary};
`;

const WaypointIcon = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.primary.main};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex-shrink: 0;
`;

const WaypointContent = styled.div`
  flex: 1;
`;

const WaypointTitle = styled.div`
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin-bottom: 0.25rem;
`;

const WaypointMeta = styled.div`
  font-size: 0.8125rem;
  color: ${theme.colors.text.secondary};
  
  span {
    margin-right: 0.75rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1.5rem;
  color: ${theme.colors.text.secondary};
  
  svg {
    width: 4rem;
    height: 4rem;
    margin: 0 auto 1rem;
    opacity: 0.5;
  }
  
  h3 {
    color: ${theme.colors.text.primary};
    margin: 0 0 0.5rem 0;
    font-size: 1.125rem;
  }
  
  p {
    margin: 0;
    font-size: 0.9375rem;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 1.5rem;
  color: ${theme.colors.text.secondary};
  font-size: 1.125rem;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 4rem 1.5rem;
  
  svg {
    width: 5rem;
    height: 5rem;
    margin: 0 auto 1.5rem;
    color: #f87171;
  }
  
  h2 {
    color: ${theme.colors.text.primary};
    margin: 0 0 1rem 0;
    font-size: 1.5rem;
  }
  
  p {
    color: ${theme.colors.text.secondary};
    margin: 0;
    font-size: 1rem;
  }
`;

const SharedTripView = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSharedTrip();
  }, [token]);

  const loadSharedTrip = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use regular fetch call without auth token - goes through Nginx proxy
      const apiUrl = `${window.location.protocol}//${window.location.hostname}${window.location.port && window.location.port !== '80' && window.location.port !== '443' ? ':' + window.location.port : ''}`;
      const response = await fetch(`${apiUrl}/api/trip-sharing/shared/${token}`);
      
      if (!response.ok) {
        throw new Error('Trip not found or expired');
      }
      
      const data = await response.json();
      setTrip(data.trip);
      
    } catch (err) {
      console.error('Error loading shared trip:', err);
      setError(err.message || 'Failed to load shared trip');
      toast.error('This trip link is invalid or has expired');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <PlayIcon />;
      case 'completed':
        return <CheckCircleIcon />;
      default:
        return <FlagIcon />;
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <Container>
          <Header>
            <Logo>
              <LogoText>BoatWise</LogoText>
            </Logo>
            <SubTitle>Shared Trip</SubTitle>
          </Header>
          <Card>
            <LoadingState>Loading shared trip...</LoadingState>
          </Card>
        </Container>
      </PageContainer>
    );
  }

  if (error || !trip) {
    return (
      <PageContainer>
        <Container>
          <Header>
            <Logo>
              <LogoText>BoatWise</LogoText>
            </Logo>
            <SubTitle>Shared Trip</SubTitle>
          </Header>
          <Card>
            <ErrorState>
              <ExclamationTriangleIcon />
              <h2>Trip Not Found</h2>
              <p>{error || 'This trip link is invalid or has expired.'}</p>
            </ErrorState>
          </Card>
        </Container>
      </PageContainer>
    );
  }

  const shareInfo = trip.share_info || {};

  return (
    <PageContainer>
      <Container>
        <Header>
          <Logo>
            <LogoText>BoatWise</LogoText>
          </Logo>
          <SubTitle>Shared Trip by {trip.owner_name}</SubTitle>
        </Header>

        {/* Trip Overview */}
        <Card>
          <CardHeader>
            <CardTitle>{trip.trip_name}</CardTitle>
            <TripMeta>
              <StatusBadge status={trip.trip_status}>
                {getStatusIcon(trip.trip_status)}
                {trip.trip_status}
              </StatusBadge>
              {trip.start_port && <span>📍 {trip.start_port}</span>}
              {trip.start_time && <span>🕐 {formatDate(trip.start_time)}</span>}
              {trip.trip_type && <span>🚤 {trip.trip_type}</span>}
            </TripMeta>
          </CardHeader>
          
          {trip.trip_description && (
            <CardContent>
              <p style={{ color: theme.colors.text.secondary, margin: 0, lineHeight: 1.6 }}>
                {trip.trip_description}
              </p>
            </CardContent>
          )}
        </Card>

        {/* Trip Statistics */}
        {shareInfo.can_view_statistics && (
          <Card>
            <CardHeader>
              <CardTitle>
                <ChartBarIcon />
                Trip Statistics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <StatsGrid>
                {trip.duration_minutes && (
                  <StatItem>
                    <StatValue>{formatDuration(trip.duration_minutes)}</StatValue>
                    <StatLabel>Duration</StatLabel>
                  </StatItem>
                )}
                
                {trip.total_distance_km !== undefined && (
                  <StatItem>
                    <StatValue>{parseFloat(trip.total_distance_km || 0).toFixed(1)}</StatValue>
                    <StatLabel>Distance (km)</StatLabel>
                  </StatItem>
                )}
                
                {trip.total_distance_nm !== undefined && (
                  <StatItem>
                    <StatValue>{parseFloat(trip.total_distance_nm || 0).toFixed(1)}</StatValue>
                    <StatLabel>Distance (nm)</StatLabel>
                  </StatItem>
                )}
                
                {trip.average_speed_knots !== undefined && (
                  <StatItem>
                    <StatValue>{parseFloat(trip.average_speed_knots || 0).toFixed(1)}</StatValue>
                    <StatLabel>Avg Speed (kts)</StatLabel>
                  </StatItem>
                )}
                
                {trip.max_speed_knots !== undefined && (
                  <StatItem>
                    <StatValue>{parseFloat(trip.max_speed_knots || 0).toFixed(1)}</StatValue>
                    <StatLabel>Max Speed (kts)</StatLabel>
                  </StatItem>
                )}
                
                {trip.total_fuel_used_liters !== undefined && (
                  <StatItem>
                    <StatValue>{parseFloat(trip.total_fuel_used_liters || 0).toFixed(1)}</StatValue>
                    <StatLabel>Fuel Used (L)</StatLabel>
                  </StatItem>
                )}
                
                {trip.total_engine_hours !== undefined && (
                  <StatItem>
                    <StatValue>{parseFloat(trip.total_engine_hours || 0).toFixed(1)}</StatValue>
                    <StatLabel>Engine Hours</StatLabel>
                  </StatItem>
                )}
              </StatsGrid>
            </CardContent>
          </Card>
        )}

        {/* Waypoints */}
        {shareInfo.can_view_location && trip.waypoints && trip.waypoints.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>
                <MapPinIcon />
                Stops & Waypoints
              </CardTitle>
            </CardHeader>
            <CardContent>
              <WaypointsList>
                {trip.waypoints.map((waypoint, index) => (
                  <WaypointItem key={index}>
                    <WaypointIcon>{index + 1}</WaypointIcon>
                    <WaypointContent>
                      <WaypointTitle>{waypoint.waypoint_name}</WaypointTitle>
                      <WaypointMeta>
                        {waypoint.location_name && <span>📍 {waypoint.location_name}</span>}
                        {waypoint.arrival_time && <span>🕐 {formatDate(waypoint.arrival_time)}</span>}
                      </WaypointMeta>
                    </WaypointContent>
                  </WaypointItem>
                ))}
              </WaypointsList>
            </CardContent>
          </Card>
        )}

        {/* Photos */}
        {shareInfo.can_view_photos && trip.photos && trip.photos.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>
                <PhotoIcon />
                Trip Photos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <PhotoGrid>
                {trip.photos.map((photo, index) => (
                  <PhotoItem key={index}>
                    <img src={photo.url} alt={photo.caption || 'Trip photo'} />
                  </PhotoItem>
                ))}
              </PhotoGrid>
            </CardContent>
          </Card>
        )}

        {/* Map Placeholder */}
        {shareInfo.can_view_location && (
          <Card>
            <CardHeader>
              <CardTitle>
                <MapPinIcon />
                Trip Route
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TripMap
                track={trip.track || []}
                waypoints={trip.waypoints || []}
                startPoint={trip.start_latitude && trip.start_longitude ? {
                  lat: trip.start_latitude,
                  lon: trip.start_longitude,
                  name: trip.start_port,
                  time: trip.start_time
                } : null}
                endPoint={trip.end_latitude && trip.end_longitude ? {
                  lat: trip.end_latitude,
                  lon: trip.end_longitude,
                  name: trip.end_port,
                  time: trip.end_time
                } : null}
                height="600px"
                showStats={true}
              />
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '3rem', paddingTop: '2rem', borderTop: `1px solid ${theme.colors.border.primary}` }}>
          <p style={{ color: theme.colors.text.secondary, fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
            Powered by <strong style={{ color: theme.colors.primary.main }}>BoatWise</strong> - Marine Dashboard Platform
          </p>
          <p style={{ color: theme.colors.text.disabled, fontSize: '0.8125rem', margin: 0 }}>
            Want to track your own trips? Visit <a href="http://boatwise.ca" style={{ color: theme.colors.primary.main, textDecoration: 'none' }}>boatwise.ca</a>
          </p>
        </div>
      </Container>
    </PageContainer>
  );
};

export default SharedTripView;

